/**
 * ReplayControls — playback transport: play/pause, scrub bar, speed
 * selector, step forward/back. Receives the playback hook directly.
 */

import type { ReactElement } from 'react';
import type { ReplayPlaybackHook } from './useReplayPlayback.js';

const SPEEDS = [0.5, 1, 2, 4] as const;

type Props = {
  hook: ReplayPlaybackHook;
};

export function ReplayControls({ hook }: Props): ReactElement {
  const { events, cursor, playing, speed, setSpeed, toggle, seekTo, stepNext, stepPrev } = hook;
  const total = events.length;

  return (
    <div className="flex w-full flex-col gap-2 rounded-md border border-cosmos-700 bg-cosmos-900/80 p-3 text-sm text-cosmos-100">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={stepPrev}
          disabled={cursor <= 0}
          className="rounded px-2 py-1 hover:bg-cosmos-700 disabled:opacity-30"
          title="Step back"
        >
          ◀
        </button>
        <button
          type="button"
          onClick={toggle}
          className="rounded bg-amber-600 px-3 py-1 font-medium text-white hover:bg-amber-500"
          title={playing ? 'Pause' : 'Play'}
        >
          {playing ? '⏸ Pause' : '▶ Play'}
        </button>
        <button
          type="button"
          onClick={stepNext}
          disabled={cursor >= total - 1}
          className="rounded px-2 py-1 hover:bg-cosmos-700 disabled:opacity-30"
          title="Step forward"
        >
          ▶
        </button>
        <span className="ml-2 font-mono text-xs text-cosmos-400">
          {cursor + 1} / {total}
        </span>
        <span className="ml-auto flex items-center gap-1">
          <span className="text-xs text-cosmos-400">Speed:</span>
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSpeed(s)}
              className={`rounded px-2 py-0.5 text-xs ${
                speed === s ? 'bg-amber-600 text-white' : 'hover:bg-cosmos-700'
              }`}
            >
              {s}×
            </button>
          ))}
        </span>
      </div>

      <input
        type="range"
        min={0}
        max={Math.max(0, total - 1)}
        value={cursor}
        onChange={(e) => seekTo(Number(e.target.value))}
        className="w-full"
        aria-label="Replay scrub bar"
      />
    </div>
  );
}
